import { memo } from 'react';
import type { TrackerItem } from '@/domain/content.types';
import { pickName } from '@/i18n/itemName';
import { useT } from '@/i18n/useT';
import { useProgress } from '@/state/progressStore';
import { useSettings } from '@/state/settingsStore';
import { MeowCheck } from './MeowCheck';
import { RarityBadge } from './RarityBadge';

interface ItemRowProps {
  setId: string;
  item: TrackerItem;
}

/**
 * One reward. Dense single line on desktop, two-line card on mobile — same
 * component, layout switched by breakpoint so there is only one thing to
 * maintain.
 *
 * Subscribes to just its own `done` flag, so ticking one row re-renders one row.
 */
export const ItemRow = memo(function ItemRow({ setId, item }: ItemRowProps) {
  const { t, lang } = useT();
  const done = useProgress((s) => s.snapshot.sets[setId]?.[item.id]?.done === true);
  const toggle = useProgress((s) => s.toggle);
  const showBothNames = useSettings((s) => s.showBothNames);

  const name = pickName(item, lang);
  const showAlt = showBothNames && name.alt && name.alt !== name.value;

  return (
    // Source track: 13rem is the floor, and above it the column takes 0.6 of
    // the leftover against the name's 1 — 295px on a full-width row, which
    // clears the longest Russian source ("Незваный гость: Возрождённый
    // Крохотный Убивец", ~282px). A flex max rather than a bigger fixed width
    // on purpose: at the md breakpoint there is nothing spare, and a fixed one
    // would buy source width by clipping names, which are the thing you
    // actually scan the row for.
    <li
      className={`grid grid-cols-[auto_1fr] items-center gap-x-2 border-b border-line-soft px-2 py-1.5 last:border-b-0 md:grid-cols-[auto_minmax(0,1fr)_minmax(13rem,0.6fr)_7.5rem] md:py-1 ${
        done ? 'bg-pip-faint/10' : 'hover:bg-surface-2/60'
      }`}
    >
      <MeowCheck
        checked={done}
        onChange={() => toggle(setId, item.id)}
        label={t('meow.aria', { name: name.value })}
      />

      <div className="min-w-0">
        <div className="flex min-w-0 items-baseline gap-1.5">
          <span
            className={`truncate text-[13px] leading-5 ${
              done ? 'text-text-faint line-through decoration-pip-dim' : 'text-text'
            } ${name.isFallback ? 'decoration-dotted underline decoration-amber/60 underline-offset-2' : ''}`}
            title={
              name.isFallback
                ? t('item.untranslated')
                : name.needsReview
                  ? `${name.alt} — ${t('item.reviewNeeded')}`
                  : name.alt
            }
          >
            {name.value}
          </span>
        </div>
        {showAlt ? (
          <div className="truncate text-[11px] leading-4 text-text-faint">{name.alt}</div>
        ) : null}
        {/* Source and chance move under the name on narrow screens. */}
        <div className="flex items-center gap-2 md:hidden">
          {item.source ? (
            <span className="min-w-0 truncate text-[11px] leading-4 text-text-faint">
              {item.source[lang]}
            </span>
          ) : null}
          <RarityBadge
            {...(item.rarity ? { rarity: item.rarity } : {})}
            {...(item.dropChance ? { dropChance: item.dropChance } : {})}
          />
        </div>
        {/*
          Shown outright rather than behind a hover tooltip. Notes carry the
          conditions that decide whether a drop is obtainable at all ("only for
          the player who used the Wanted Poster"), which is no use hidden — and
          a hover target does not exist on the phone people actually play with.
          Last line because it qualifies the row rather than identifying it, and
          it wraps rather than truncating: a half-shown caveat is worse than none.
        */}
        {item.notes ? (
          <p className="mt-0.5 border-l-2 border-line pl-1.5 text-[11px] leading-4 text-text-faint">
            {item.notes[lang]}
          </p>
        ) : null}
      </div>

      <div className="hidden min-w-0 truncate text-[11px] leading-4 text-text-faint md:block">
        {item.source?.[lang] ?? ''}
      </div>

      <div className="hidden justify-self-start md:block">
        <RarityBadge
          {...(item.rarity ? { rarity: item.rarity } : {})}
          {...(item.dropChance ? { dropChance: item.dropChance } : {})}
        />
      </div>
    </li>
  );
});
